import { z } from 'zod';
import { AccountDrilldownTabSchema } from '../account-drilldown-tabs';

/**
 * Flow layouts: the presentational skin of a Flow surface (labels, alignment,
 * name cell, row numbers, density, default widths, account tabs, Home
 * columns). A layout is presentational EXCEPT that it may declare
 * `fixedColumns` for a table: those keys render first, in that order, and the
 * rep's own columns follow. The rep's saved column list is never rewritten.
 * A UI View (`claydr | gtme | gs`) only sets the DEFAULT layout per surface;
 * a rep's own choice always wins over it.
 */

/** The Flow tables a list layout can apply to. Declared here, not imported from `mini-apps`. */
export const FlowLayoutTableSchema = z.enum(['companies', 'people', 'plays', 'signals']);
export type FlowLayoutTable = z.infer<typeof FlowLayoutTableSchema>;

/** A working-pick slot: a list table, or one of the two page surfaces. */
export const FlowLayoutSlotSchema = z.union([FlowLayoutTableSchema, z.literal('account'), z.literal('home')]);
export type FlowLayoutSlot = z.infer<typeof FlowLayoutSlotSchema>;

/** Separate enums per surface so a surface can grow a layout the others do not have. */
export const FlowListLayoutIdSchema = z.enum(['cdr', 'gs', 'signal-response']);
export type FlowListLayoutId = z.infer<typeof FlowListLayoutIdSchema>;

export const FlowAccountLayoutIdSchema = z.enum(['cdr', 'gs']);
export type FlowAccountLayoutId = z.infer<typeof FlowAccountLayoutIdSchema>;

export const FlowHomeLayoutIdSchema = z.enum(['cdr', 'gs']);
export type FlowHomeLayoutId = z.infer<typeof FlowHomeLayoutIdSchema>;

/** A rep's list-layout choice per table (the `User.prefFlowLayoutList` JSON column). */
export const FlowListLayoutChoicesSchema = z.partialRecord(FlowLayoutTableSchema, FlowListLayoutIdSchema);
export type FlowListLayoutChoices = z.infer<typeof FlowListLayoutChoicesSchema>;

/** Where a resolved layout came from, for the menu's source badge. */
export const FlowLayoutSourceSchema = z.enum(['working', 'saved', 'view-pin', 'ui-view-default', 'fallback']);
export type FlowLayoutSource = z.infer<typeof FlowLayoutSourceSchema>;

/** A cell renderer a layout may select per column; the table's caller maps each variant to a component. */
export const FlowCellVariantSchema = z.enum([
  'gs-account',
  'pace-stacked',
  'signal-stacked',
  'contact-stacked',
  'account-stacked',
  'local-time-stacked',
  'owners-stacked',
  'flow-stacked',
  'step-since',
]);
export type FlowCellVariant = z.infer<typeof FlowCellVariantSchema>;

/** One field a stacked cell shows: a filter-catalog key and the short word used in the header. */
export const FlowColumnStackPartSchema = z.object({ key: z.string(), short: z.string() });
export type FlowColumnStackPart = z.infer<typeof FlowColumnStackPartSchema>;

/** A Home board column. The ids double as the `column` value on Home analytics events (a data contract), so they are snake_case. */
export const FlowHomeColumnIdSchema = z.enum(['cold_accounts', 'open_loops', 'todays_meetings']);
export type FlowHomeColumnId = z.infer<typeof FlowHomeColumnIdSchema>;

export const FlowListLayoutSpecSchema = z.object({
  id: FlowListLayoutIdSchema,
  /** Short name, used on the trigger ("Layout: Health"). */
  name: z.string(),
  /** Who the layout is for, shown muted after the name in the menu: "(Post-Sales)". */
  audience: z.string(),
  appliesTo: FlowLayoutTableSchema.array().readonly(),
  /** Column label overrides per table, keyed by the table's field key. Labels only, never keys. */
  columnLabels: z.partialRecord(FlowLayoutTableSchema, z.record(z.string(), z.string())),
  /** Default column widths per table; a width the user dragged wins. */
  defaultWidths: z.partialRecord(FlowLayoutTableSchema, z.record(z.string(), z.number())),
  /** Cell renderer variant per table and column key. */
  cells: z.partialRecord(FlowLayoutTableSchema, z.record(z.string(), FlowCellVariantSchema)),
  /** Per-column alignment overrides per table; wins over `alignAllButFirst`. */
  columnAlign: z.partialRecord(FlowLayoutTableSchema, z.record(z.string(), z.enum(['left', 'center']))),
  alignAllButFirst: z.enum(['center']).nullable(),
  showRowNumbers: z.boolean(),
  /** Row density per table; absent = dense. */
  rowDensity: z.partialRecord(FlowLayoutTableSchema, z.enum(['dense', 'tall', 'roomy'])),
  showTracker: z.boolean(),
  /**
   * Column keys the table renders first, in this order, per table. The rep's
   * own columns render after them; the saved column list is never rewritten.
   */
  fixedColumns: z.partialRecord(FlowLayoutTableSchema, z.string().array().readonly()).optional(),
  /**
   * The fields a stacked cell shows, in display order, per table and column key;
   * drives the header text and the '?' tooltip. Keys are filter-catalog keys.
   */
  columnStacks: z.partialRecord(FlowLayoutTableSchema, z.record(z.string(), FlowColumnStackPartSchema.array().readonly())).optional(),
});
export type FlowListLayoutSpec = z.infer<typeof FlowListLayoutSpecSchema>;

export const FlowAccountLayoutSpecSchema = z.object({
  id: FlowAccountLayoutIdSchema,
  /** Short name, used on the trigger ("Layout: Health"). */
  name: z.string(),
  /** Who the layout is for, shown muted after the name in the menu: "(Post-Sales)". */
  audience: z.string(),
  tabs: AccountDrilldownTabSchema.array().readonly(),
  defaultTab: AccountDrilldownTabSchema,
});
export type FlowAccountLayoutSpec = z.infer<typeof FlowAccountLayoutSpecSchema>;

export const FlowHomeLayoutSpecSchema = z.object({
  id: FlowHomeLayoutIdSchema,
  /** Short name, used on the trigger ("Layout: Health"). */
  name: z.string(),
  /** Who the layout is for, shown muted after the name in the menu: "(Post-Sales)". */
  audience: z.string(),
  columns: FlowHomeColumnIdSchema.array().readonly(),
  cardFacts: z.enum(['health', 'pace', 'contract-end']).array().readonly(),
  showDealBrief: z.boolean(),
  loopLink: z.enum(['account-open-loops', 'open-loops-page']),
});
export type FlowHomeLayoutSpec = z.infer<typeof FlowHomeLayoutSpecSchema>;

/** A UI View's default layout per surface, plus an optional per-table list override. */
export const UiViewFlowLayoutsSchema = z.object({
  defaults: z.object({
    list: FlowListLayoutIdSchema,
    account: FlowAccountLayoutIdSchema,
    home: FlowHomeLayoutIdSchema,
  }),
  listByTable: FlowListLayoutChoicesSchema.optional(),
});
export type UiViewFlowLayouts = z.infer<typeof UiViewFlowLayoutsSchema>;
